import { Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Brand from "@/components/Brand";
import usePageMeta from "@/hooks/usePageMeta";
import api from "@/lib/api";
import {
  Clock, MapPin, BarChart3, Users, Wallet, Shield, ListChecks,
  Camera, CalendarCheck, ChevronRight, Sparkles, Check, X, Mail,
  Package, UserPlus, Gauge,
} from "lucide-react";

const PROD_ORIGIN = "https://hrbo.in";

const FEATURES = [
  {
    icon: MapPin,
    title: "Geo-fenced punch-in",
    body: "Lock attendance to a 50-meter radius of each outlet. GPS coordinates and selfies are logged on every session, so payroll only ever counts genuine on-site work.",
  },
  {
    icon: Camera,
    title: "Selfie verification",
    body: "Every punch-in and punch-out captures a camera selfie. Stored against the session for fraud-proof audit trails — no buddy punching, no disputes.",
  },
  {
    icon: Wallet,
    title: "Payroll for any cycle",
    body: "Run payroll for calendar months OR fully custom date ranges (20th → 19th, weekly, fortnightly). Mid-cycle joiners and leavers are automatically pro-rated.",
  },
  {
    icon: Shield,
    title: "RBAC across the org",
    body: "Granular roles — Company Admin, HR, Accountant, Cluster Manager, Manager and Employee — each with scoped access. A reporting-manager hierarchy builds your full org structure.",
  },
  {
    icon: Gauge,
    title: "Performance reviews",
    body: "Managers set goals and rate reportees 1–5 with comments; employees see only their overall rating and acknowledge it. Dashboards, trends and org-wide summaries with auto-reminders.",
  },
  {
    icon: Package,
    title: "Asset management",
    body: "Track company assets bound to outlets and employees, with transfer histories, service and AMC records, and exit-blocking so no one leaves holding kit.",
  },
  {
    icon: UserPlus,
    title: "Employee referrals",
    body: "HR sets grade-wise bonuses and serving periods; staff refer candidates and approved bonuses flow straight into the next payroll run — with in-app notifications throughout.",
  },
  {
    icon: ListChecks,
    title: "Tasks with threaded chat",
    body: "Assign tasks to anyone in the company. Each task has its own multi-reply discussion thread so context never gets lost across email and DMs.",
  },
  {
    icon: BarChart3,
    title: "Reports & exports",
    body: "Filter attendance, payroll, assets and referrals by date range, outlet or employee. One-click CSV export for accounting, audits and board reviews.",
  },
];

const STEPS = [
  { n: "01", t: "Create your tenant", d: "Spin up an isolated workspace and onboard your first admin in under a minute." },
  { n: "02", t: "Add outlets & employees", d: "Mark each outlet's GPS, define roles, and invite employees with phone-based logins." },
  { n: "03", t: "Punch, process, pay", d: "Employees punch in with selfies; you run payroll, mark payments, export to your accounting tool." },
];

export default function LandingPage() {
  const { user, loading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly"); // 'monthly' | 'yearly'
  const [contactOpen, setContactOpen] = useState(false);
  usePageMeta({
    title: "HRBo — SaaS attendance & payroll with geo-fenced punch-in",
    description:
      "HRBo is a multi-tenant attendance & payroll platform for SMBs and retail chains. Geo-fenced selfie punch-in, RBAC, threaded task chats, and payroll for any cycle — all in one workspace.",
    canonical: `${PROD_ORIGIN}/`,
  });

  useEffect(() => {
    api.get("/plans/public").then((r) => setPlans((r.data || []).filter((p) => p.active))).catch(() => setPlans([]));
  }, []);

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (user) return <Navigate to="/portal" replace />;

  return (
    <div className="min-h-screen bg-white text-slate-950" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <Brand size="sm" to={null} testid="landing-brand" />
          <nav className="flex items-center gap-2 sm:gap-4">
            <a href="#features" className="hidden sm:inline text-sm text-slate-600 hover:text-slate-950" data-testid="nav-features">Features</a>
            <a href="#how" className="hidden sm:inline text-sm text-slate-600 hover:text-slate-950" data-testid="nav-how">How it works</a>
            <a href="#pricing" className="hidden sm:inline text-sm text-slate-600 hover:text-slate-950" data-testid="nav-pricing">Pricing</a>
            <button onClick={() => setContactOpen(true)} className="hidden sm:inline text-sm text-slate-600 hover:text-slate-950" data-testid="nav-contact">Contact</button>
            <Link to="/login" className="text-sm text-slate-700 hover:text-slate-950 px-3 py-1.5" data-testid="landing-login-link">Sign in</Link>
            <Link to="/signup" data-testid="landing-signup-link">
              <Button size="sm" className="bg-slate-950 hover:bg-slate-800 rounded-sm">Get started</Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Workforce OS · 2026
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Seamless<br />Attendance.<br />
              <span className="text-slate-400">Precise Payroll.</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-7 max-w-2xl leading-relaxed">
              HRBo gives multi-outlet teams a single workspace for geo-fenced punch-ins, automated
              payroll for any cycle, threaded task chats and SaaS-grade tenant isolation. Built for
              modern HR, finance and operations teams who would rather ship than reconcile spreadsheets.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-signup-btn">
                <Button size="lg" className="bg-slate-950 hover:bg-slate-800 rounded-sm h-12 px-6">
                  Create your tenant <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-login-btn">
                <Button size="lg" variant="outline" className="rounded-sm h-12 px-6">Sign in</Button>
              </Link>
            </div>
            <p className="text-xs text-slate-500 mt-4">Free during preview · No credit card · 30-second onboarding</p>
          </div>

          {/* Hero stats card */}
          <div className="lg:col-span-5">
            <div className="bg-slate-950 text-white p-8 rounded-sm shadow-xl">
              <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-6">Today, across HRBo tenants</div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-4xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>50m</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Geo-fence radius</div>
                </div>
                <div>
                  <div className="text-4xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>RBAC</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">5 roles</div>
                </div>
                <div>
                  <div className="text-4xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>Any</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">Payroll cycle</div>
                </div>
                <div>
                  <div className="text-4xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>1-click</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">CSV export</div>
                </div>
              </div>
              <div className="border-t border-slate-800 mt-7 pt-5 flex items-center gap-4 text-xs text-slate-400">
                <Clock className="w-4 h-4" /> Multi-session daily punches
                <span className="hidden sm:inline">·</span>
                <CalendarCheck className="w-4 h-4 hidden sm:inline" /> <span className="hidden sm:inline">Pro-rated mid-cycle</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-3">What you get</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>
              The whole attendance-to-payroll loop, in one workspace.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Most HR tools cover one slice — clock-in, or leaves, or payroll. HRBo joins them.
              Every punch, every approved leave, every advance and every custom deduction flows
              automatically into one payroll number. No copy-paste, no reconciliation.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {FEATURES.map((f) => (
              <div key={f.title} className="border border-slate-200 p-6 hover:border-slate-400 transition-colors" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <f.icon className="w-6 h-6 text-slate-950 mb-4" strokeWidth={1.5} />
                <div className="font-semibold text-slate-950">{f.title}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-3">How it works</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>
                Go from spreadsheet to live payroll in three steps.
              </h2>
              <p className="text-slate-600 mt-4 leading-relaxed">
                There&apos;s no implementation consultant, no SQL migration, no 90-day onboarding deck.
                You sign up, mark your outlets on a map, invite your team — and the next payroll
                runs itself.
              </p>
              <Link to="/signup" className="inline-block mt-6" data-testid="how-signup-btn">
                <Button className="bg-slate-950 hover:bg-slate-800 rounded-sm">Start your tenant</Button>
              </Link>
            </div>
            <ol className="space-y-4">
              {STEPS.map((s) => (
                <li key={s.n} className="bg-white border border-slate-200 p-6 flex gap-5" data-testid={`step-${s.n}`}>
                  <div className="text-2xl font-bold tracking-tighter text-slate-300" style={{ fontFamily: "Outfit, sans-serif" }}>{s.n}</div>
                  <div>
                    <div className="font-semibold text-slate-950">{s.t}</div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <div className="grid lg:grid-cols-3 gap-8">
            <div>
              <Users className="w-6 h-6 mb-3" strokeWidth={1.5} />
              <div className="font-semibold text-slate-950">Retail & F&amp;B chains</div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Multiple outlets, hourly staff, daily attendance variance — HRBo handles outlet-scoped
                shifts and consolidates payroll across all locations.
              </p>
            </div>
            <div>
              <Wallet className="w-6 h-6 mb-3" strokeWidth={1.5} />
              <div className="font-semibold text-slate-950">SMB HR teams</div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Stop juggling Excel for leave balances, PF calculations and salary advances. HRBo
                bakes them into one engine that auditors will actually like.
              </p>
            </div>
            <div>
              <Shield className="w-6 h-6 mb-3" strokeWidth={1.5} />
              <div className="font-semibold text-slate-950">Field-services agencies</div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Selfies + GPS on every punch means you can prove a technician was on-site without
                installing a separate field-management tool.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-3">Pricing</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>
              Simple plans that scale with your headcount.
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              Pay a flat fee for the first cohort of employees, then a small per-employee-per-month
              charge after that. Yearly billing saves you 20%.
            </p>
            {/* Monthly / Yearly toggle */}
            <div className="mt-7 inline-flex border border-slate-300 bg-white rounded-sm overflow-hidden" data-testid="pricing-cycle-toggle">
              <button
                onClick={() => setCycle("monthly")}
                className={`px-4 py-2 text-sm uppercase tracking-widest ${cycle === "monthly" ? "bg-slate-950 text-white" : "text-slate-700"}`}
                data-testid="cycle-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setCycle("yearly")}
                className={`px-4 py-2 text-sm uppercase tracking-widest ${cycle === "yearly" ? "bg-slate-950 text-white" : "text-slate-700"}`}
                data-testid="cycle-yearly"
              >
                Yearly · Save 20%
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            {plans.map((p) => {
              const discount = cycle === "yearly" ? (1 - (p.yearly_discount || 0)) : 1;
              const monthlyEffective = (p.base_price_monthly || 0) * discount;
              const yearlyTotal = monthlyEffective * 12;
              const isPro = p.slug === "professional";
              return (
                <div
                  key={p.id}
                  className={`p-7 border ${isPro ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white"}`}
                  data-testid={`pricing-card-${p.slug}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-lg font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{p.name}</div>
                    {isPro && <span className="text-[10px] uppercase tracking-widest bg-white text-slate-950 px-2 py-0.5">Popular</span>}
                  </div>
                  <p className={`text-sm leading-relaxed mb-5 ${isPro ? "text-slate-300" : "text-slate-600"}`}>{p.tagline || "—"}</p>

                  {p.is_contact_only ? (
                    <div className="mb-5">
                      <div className="text-4xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>Talk to us</div>
                      <div className={`text-xs uppercase tracking-widest mt-1 ${isPro ? "text-slate-400" : "text-slate-500"}`}>Tailored quote</div>
                    </div>
                  ) : (
                    <div className="mb-5">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>
                          ₹{monthlyEffective.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                        <span className={`text-sm ${isPro ? "text-slate-400" : "text-slate-500"}`}>/mo</span>
                      </div>
                      <div className={`text-xs ${isPro ? "text-slate-400" : "text-slate-500"} mt-1`}>
                        {p.base_employee_limit === 0 ? "Unlimited employees" : `Up to ${p.base_employee_limit} employees`}
                        {p.overage_pepm > 0 && ` · ₹${p.overage_pepm}/extra employee/mo`}
                      </div>
                      {cycle === "yearly" && (
                        <div className="text-[11px] mt-1 text-emerald-400">Save 20% — ₹{yearlyTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}/yr</div>
                      )}
                    </div>
                  )}

                  <ul className="space-y-2 mb-6">
                    {Object.entries(p.modules || {}).map(([k, v]) => (
                      <li key={k} className={`flex items-center gap-2 text-sm ${isPro ? "text-slate-200" : "text-slate-700"}`}>
                        {v
                          ? <Check className="w-4 h-4 text-emerald-500" />
                          : <X className={`w-4 h-4 ${isPro ? "text-slate-600" : "text-slate-300"}`} />}
                        <span className={!v ? "line-through opacity-60" : ""}>
                          {k === "multi_outlet" ? "Multi-Outlet Tracking"
                            : k === "tasks" ? "Task Management"
                            : k.charAt(0).toUpperCase() + k.slice(1)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {p.is_contact_only ? (
                    <Button onClick={() => setContactOpen(true)} className={`w-full rounded-sm ${isPro ? "bg-white text-slate-950 hover:bg-slate-100" : "bg-slate-950 hover:bg-slate-800"}`} data-testid={`pricing-cta-${p.slug}`}>
                      <Mail className="w-4 h-4 mr-1" /> Contact us
                    </Button>
                  ) : (
                    <Link to="/signup" data-testid={`pricing-cta-${p.slug}`}>
                      <Button className={`w-full rounded-sm ${isPro ? "bg-white text-slate-950 hover:bg-slate-100" : "bg-slate-950 hover:bg-slate-800"}`}>
                        Start 30-day free trial <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
              );
            })}
            {plans.length === 0 && (
              <div className="lg:col-span-3 text-center text-sm text-slate-500 py-10">Loading plans…</div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter" style={{ fontFamily: "Outfit, sans-serif" }}>
            Run next month&apos;s payroll on HRBo.
          </h2>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">
            Free during preview. No credit card, no setup call. Spin up your tenant in 30 seconds
            and pay your team from the same screen.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/signup" data-testid="cta-signup-btn">
              <Button size="lg" className="bg-white text-slate-950 hover:bg-slate-100 rounded-sm h-12 px-6">
                Create your tenant <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/login" data-testid="cta-login-btn">
              <Button size="lg" variant="outline" className="rounded-sm h-12 px-6 border-slate-700 text-slate-200 hover:bg-slate-900 hover:text-white">
                Sign in
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 uppercase tracking-[0.2em]">
          <span>HRBo · Workforce OS · 2026</span>
          <div className="flex items-center gap-4">
            <a href="#pricing" className="hover:text-slate-950" data-testid="footer-pricing">Pricing</a>
            <button onClick={() => setContactOpen(true)} className="hover:text-slate-950 uppercase tracking-[0.2em]" data-testid="footer-contact">Contact</button>
            <Link to="/login" className="hover:text-slate-950" data-testid="footer-login">Sign in</Link>
            <Link to="/signup" className="hover:text-slate-950" data-testid="footer-signup">Sign up</Link>
            <Link to="/privacy-policy" className="hover:text-slate-950" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-950" data-testid="footer-terms">Terms</Link>
          </div>
        </div>
      </footer>

      {/* Contact-us dialog (Custom plan CTA) */}
      {contactOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center px-4" onClick={() => setContactOpen(false)} data-testid="contact-modal">
          <div className="bg-white rounded-sm max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Let&apos;s talk</h3>
              <button onClick={() => setContactOpen(false)} className="text-slate-500 hover:text-slate-950" data-testid="contact-close">✕</button>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Custom plans are perfect for teams of 100+ employees, multi-region rollouts, SSO,
              SLA-backed support and integrations with your existing accounting stack.
              Drop us a note and we&apos;ll get back within a business day.
            </p>
            <div className="mt-5 space-y-2 text-sm">
              <a href="mailto:info@hrbo.in?subject=HRBo%20Custom%20Plan%20Enquiry" className="flex items-center gap-2 border border-slate-200 px-4 py-3 hover:border-slate-400" data-testid="contact-email-link">
                <Mail className="w-4 h-4 text-slate-500" /> info@hrbo.in
              </a>
              <a href="https://wa.me/917338299928?text=Hi%20HRBo%2C%20I%27d%20like%20a%20Custom%20plan%20quote." target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-slate-200 px-4 py-3 hover:border-slate-400" data-testid="contact-wa-link">
                <svg className="w-4 h-4 text-slate-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M19 5A10 10 0 0 0 3 17l-1 5 5-1A10 10 0 1 0 19 5zm-7 17a8 8 0 0 1-4.3-1.3l-.3-.2-3 .7.7-3-.2-.3A8 8 0 1 1 12 22z" /></svg>
                WhatsApp business chat · +91 73382 99928
              </a>
            </div>
          </div>
        </div>
      )}
      {/* Floating WhatsApp business chat — always visible on the landing */}
      <a
        href="https://wa.me/917338299928?text=Hi%20HRBo%2C%20I%27d%20like%20to%20chat%20about%20your%20plans."
        target="_blank"
        rel="noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white shadow-lg rounded-full pl-3 pr-4 py-3 transition-transform hover:scale-105"
        data-testid="floating-wa-btn"
      >
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M19 5A10 10 0 0 0 3 17l-1 5 5-1A10 10 0 1 0 19 5zm-7 17a8 8 0 0 1-4.3-1.3l-.3-.2-3 .7.7-3-.2-.3A8 8 0 1 1 12 22zm4.6-5.9c-.3-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.6.8-.8 1-.1.2-.3.2-.5.1-.3-.1-1.2-.4-2.2-1.3-.8-.7-1.4-1.6-1.5-1.9-.2-.3 0-.4.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.2-.5 0-.2 0-.4-.1-.5-.1-.1-.6-1.4-.8-1.9-.2-.5-.4-.5-.6-.5h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2s.9 2.6 1.1 2.8c.1.2 1.9 2.9 4.6 4 2.7 1.1 2.7.7 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.2-.3-.2-.6-.4z" />
        </svg>
        <span className="hidden sm:inline text-sm font-semibold">Chat on WhatsApp</span>
      </a>
    </div>
  );
}
